import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { HotToastService } from '@ngneat/hot-toast';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { TestSessionForRegistrationDto } from 'src/app/core/dtos/dto';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { DateTimeHelper } from 'src/app/core/services/helpers/date-time.helper';
import { LoadingService } from 'src/app/core/services/loading.service';
import { RegistrationService } from 'src/app/core/services/registration.service';
import { ShoppingCartService } from 'src/app/core/services/shopping-cart.service';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { selectAuth } from 'src/app/state/auth.selectors';
import { loadShoppingCart } from 'src/app/state/shopping-cart.actions';

@Component({
  selector: 'app-sitting-list',
  templateUrl: './sitting-list.component.html',
  styleUrls: ['./sitting-list.component.scss']
})
export class SittingListComponent implements OnInit, OnDestroy {

  @Input() sittings: TestSessionForRegistrationDto[];
  @Input() webDelivery: boolean;
  @Input() testTypeId: number;
  @Input() isPrivate: boolean;
  @Input() parentAccountSignedIn: boolean;
  @Output() selectSitting: EventEmitter<TestSessionForRegistrationDto> = new EventEmitter();

  @ViewChild('warningSelectionModal') warningSelectionModal: ModalComponent;

  selectedSitting: TestSessionForRegistrationDto;
  selectedSittingId: number;
  existingRegShoppingCartItemId: number;
  affiliateTestCentreName: string;
  localTimeZone: string;
  warningModalTitle: string;
  warningModalBody: string;
  validateTestSessionSubscription: Subscription;
  removeFromCartSubscription: Subscription;
  authSubscription: Subscription;
  isLoggedIn: boolean;

  constructor(
    private dateTimeHelper: DateTimeHelper,
    private registrationService: RegistrationService,
    private toastService: HotToastService,
    private loadingService: LoadingService,
    private shoppingCartService: ShoppingCartService,
    private store: Store
  ) { }

  ngOnInit(): void {
    if (this.webDelivery) {
      this.localTimeZone = this.dateTimeHelper.getLocalTimeZone();
      this.sittings.forEach(sitting => sitting.testDateTimeUtc = this.dateTimeHelper.convertUTCDateToLocalDate(new Date(sitting.testDateTimeUtc)));
    }
    this.authSubscription = this.store.pipe(select(selectAuth))
      .subscribe(result => {
        this.isLoggedIn = result !== null;
      });
  }

  onSelect(sitting: TestSessionForRegistrationDto) {
    this.selectedSitting = sitting;
    this.selectedSittingId = sitting.id;
  }

  confirmSelect(sitting: TestSessionForRegistrationDto) {
    if (this.isLoggedIn) {
      this.validateTestSitting(sitting);
    }
    else {
      this.selectSitting.emit(sitting);
    }
  }

  validateTestSitting(sitting: TestSessionForRegistrationDto) {
    this.loadingService.show();
    this.validateTestSessionSubscription = this.registrationService.validateTestSession(this.testTypeId, sitting.id)
      .subscribe(
        result => {
          if (result.content.existingRegTestSessionId !== null && result.content.existingRegTestSessionId !== sitting.id) {
            let locationandDate = "";
            this.existingRegShoppingCartItemId = result.content.existingRegShoppingCartItemId;
            if (result.content.webDeliveryReg) {
              locationandDate = "Online Test Date: " + this.dateTimeHelper.formatBrowserDate(result.content.testDateTimeUtc) + "<br/>"
                + "(by time zone: " + this.dateTimeHelper.getLocalTimeZone() + ")<br/><br/>";
            }
            else {
              locationandDate = "Location: " + result.content.existingRegTestLocation + "<br/>"
                + "Date: " + result.content.existingRegTestDateTime + "<br/><br/>";
            }
            this.warningModalTitle = "Select Test";
            this.warningModalBody = "Please note that you already have a test registration in your shopping cart for:<br/><br/>"
              + locationandDate
              + "Selecting a different test will remove this existing test from your shopping cart.<br/><br/>Would you like to continue?";
            this.warningSelectionModal.open();
          }
          else {
            this.selectSitting.emit(sitting);
          }
          this.loadingService.hide();
        },
        (error: HttpErrorResponse) => {
          this.loadingService.hide();
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
          else {
            this.toastService.warning("Failed to select the test sitting because of unknown problem. Please contact us for support.");
          }
        });
  }

  removeExistingSitting() {
    this.removeFromCartSubscription = this.shoppingCartService.removeShoppingCartItem(this.existingRegShoppingCartItemId)
      .subscribe(
        result => {
          this.store.dispatch(loadShoppingCart({ shoppingCart: result.content }));
          this.selectSitting.emit(this.selectedSitting);
        },
        () => {
          this.toastService.warning("Error removing item from shopping cart");
        }
      );
  }

  ngOnDestroy() {
    this.validateTestSessionSubscription?.unsubscribe();
    this.removeFromCartSubscription?.unsubscribe();
  }
}
